import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  LoadingOutlined,
  LockOutlined,
  SyncOutlined,
  TranslationOutlined,
  YoutubeOutlined,
} from "@ant-design/icons"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd"
import dayjs from "dayjs"
import { useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { type VideoStatus, videoService, videoStatuses } from "@/services/video/videoApi"
import { VideoPlayer } from "./components/VideoPlayer"
import { useVideoTranslation } from "./useVideoTranslation"
import { formatVideoTime } from "./utils"

const { Title } = Typography

export function VideoDetailPage() {
  const { id } = useParams<{ id: string }>()
  if (!id || !/^[1-9]\d*$/.test(id)) {
    return (
      <div className="p-4">
        <Alert
          type="error"
          showIcon
          message="ID video không hợp lệ"
          action={<Link to="/videos">Về danh sách video</Link>}
        />
      </div>
    )
  }
  return <VideoDetail key={id} id={id} />
}

function VideoDetail({ id }: { id: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const detail = useQuery({
    queryKey: ["video-detail", id],
    queryFn: () => videoService.detail(id),
    retry: false,
    refetchOnWindowFocus: false,
  })

  const subtitles = useQuery({
    queryKey: ["video-subtitles", id],
    queryFn: () => videoService.subtitles(id),
    retry: false,
    refetchOnWindowFocus: false,
  })

  const [busy, setBusy] = useState(false)

  const translation = useVideoTranslation(
    id,
    subtitles.data,
    !busy && !!detail.data && !subtitles.isFetching && !detail.isFetching
  )

  const video = detail.data
  const data = subtitles.data

  const refresh = async () => {
    await Promise.all([detail.refetch(), subtitles.refetch(), queryClient.invalidateQueries({ queryKey: ["videos"] })])
  }

  const handleStatusChange = async (value: VideoStatus) => {
    setBusy(true)
    try {
      await videoService.update(id, { status: value })
      message.success(
        value === "published"
          ? "Đã xuất bản video thành công!"
          : value === "draft"
            ? "Đã chuyển video về bản nháp"
            : "Đã lưu trữ video"
      )
      await refresh()
    } catch (cause) {
      message.error(cause instanceof Error ? cause.message : "Cập nhật trạng thái thất bại.")
    } finally {
      setBusy(false)
    }
  }

  const handleReimport = async () => {
    setBusy(true)
    try {
      await videoService.reimport(id)
      message.success("Đã lấy lại phụ đề gốc từ YouTube!")
      await detail.refetch()
      const next = await subtitles.refetch()
      if (next.data) void translation.start(next.data)
      void queryClient.invalidateQueries({ queryKey: ["videos"] })
    } catch (cause) {
      message.error(cause instanceof Error ? cause.message : "Lấy phụ đề thất bại.")
    } finally {
      setBusy(false)
    }
  }

  if (detail.isPending || subtitles.isPending) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spin size="large" tip="Đang tải dữ liệu video..." />
      </div>
    )
  }

  if (!video) {
    return (
      <div className="p-4">
        <Alert
          type="error"
          showIcon
          message="Không tìm thấy video"
          description={detail.error?.message || "Video không tồn tại hoặc đã bị xóa."}
          action={
            <Button type="primary" onClick={() => navigate("/videos")}>
              Quay lại danh sách
            </Button>
          }
        />
      </div>
    )
  }

  const publishReason = !video.duration
    ? "Thời lượng video phải lớn hơn 0."
    : !data?.total
      ? "Video chưa có dữ liệu phụ đề."
      : data.remaining > 0
        ? `Cần dịch đầy đủ tất cả ${data.total} đoạn trước khi xuất bản (còn ${data.remaining} đoạn).`
        : ""

  return (
    <div className="flex flex-col gap-3 pb-8">
      {/* Top Bar with Navigation & Title */}
      <Card size="small" className="shadow-2xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/videos")}
              className="text-gray-500 hover:text-gray-800"
            >
              Danh sách video
            </Button>
            <Divider orientation="vertical" />
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-400">#{video.id}</span>
              <Title level={4} className="!m-0 text-gray-800">
                {video.name}
              </Title>
              <Tag color={video.status === "published" ? "green" : video.status === "draft" ? "orange" : "default"}>
                {videoStatuses.find((item) => item.value === video.status)?.label || video.status}
              </Tag>
            </div>
          </div>

          {/* Action Toolbar */}
          <Space wrap>
            {data &&
              data.remaining > 0 &&
              (translation.running ? (
                <Button danger icon={<LoadingOutlined />} onClick={translation.stop}>
                  Dừng dịch ({data.translated}/{data.total})
                </Button>
              ) : (
                <Button
                  type="dashed"
                  icon={<TranslationOutlined />}
                  disabled={busy}
                  onClick={() => translation.start()}
                >
                  Dịch tiếng Việt ({data.translated}/{data.total})
                </Button>
              ))}

            <Popconfirm
              title="Lấy lại phụ đề gốc từ YouTube?"
              description="Video sẽ được chuyển về bản nháp và thay thế toàn bộ bản dịch hiện tại."
              onConfirm={handleReimport}
              okText="Lấy lại"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
              disabled={busy || translation.running}
            >
              <Button icon={<SyncOutlined />} loading={busy} disabled={busy || translation.running}>
                Lấy lại phụ đề
              </Button>
            </Popconfirm>

            {video.status !== "published" && (
              <Tooltip title={publishReason}>
                <span>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    disabled={Boolean(publishReason) || busy || translation.running}
                    onClick={() => handleStatusChange("published")}
                  >
                    Xuất bản
                  </Button>
                </span>
              </Tooltip>
            )}

            {video.status !== "draft" && (
              <Button disabled={busy || translation.running} onClick={() => handleStatusChange("draft")}>
                Chuyển về nháp
              </Button>
            )}

            {video.status !== "archived" && (
              <Button disabled={busy || translation.running} onClick={() => handleStatusChange("archived")}>
                Lưu trữ
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* Metadata Descriptions */}
      <Card size="small" className="shadow-2xs">
        <Descriptions
          size="small"
          bordered
          column={{ xs: 1, sm: 2, md: 3, lg: 6 }}
          items={[
            {
              key: "source",
              label: "Kênh nguồn",
              children: (
                <a
                  href={`https://www.youtube.com/watch?v=${video.source_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-mono text-red-600 hover:underline"
                >
                  <YoutubeOutlined />
                  {video.source_id}
                </a>
              ),
            },
            {
              key: "duration",
              label: "Thời lượng",
              children: (
                <Tag icon={<ClockCircleOutlined />} color="blue">
                  {formatVideoTime(video.duration)}
                </Tag>
              ),
            },
            {
              key: "segments",
              label: "Số câu thoại",
              children: <span className="font-semibold">{video.segment_count} câu</span>,
            },
            {
              key: "access",
              label: "Truy cập",
              children: video.is_public ? (
                <Tag icon={<GlobalOutlined />} color="green">
                  Công khai
                </Tag>
              ) : (
                <Tag icon={<LockOutlined />} color="default">
                  Riêng tư
                </Tag>
              ),
            },
            {
              key: "creator",
              label: "Người tạo",
              children: video.creator?.full_name || video.creator?.username || "—",
            },
            {
              key: "created_at",
              label: "Ngày tạo",
              children: video.created_at ? dayjs(video.created_at).format("DD/MM/YYYY HH:mm") : "—",
            },
          ]}
        />
      </Card>

      {/* Main Interactive Player & Transcript */}
      {data && (
        <Card className="shadow-2xs">
          <VideoPlayer
            key={data.document.source.rawPath || video.source_id}
            sourceId={video.source_id}
            segments={data.document.segments}
          />
        </Card>
      )}
    </div>
  )
}
